import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import ResizeObserver from 'rc-resize-observer';
import classnames from 'classnames';
import BaseTable, {
    connect,
    useStore,
    useResize,
    useIndex,
    useCheckbox,
    useSort,
    useExpandRow,
    useTreeTable,
    useFilter,
    usePagination,
    useErrorTips,
    useRowClick,
    useLocation,
    useContextMenu,
    useCellSelect,
    useHotKey,
    useDOM,
    useCardTable,
    useCache,
    useStyle,
    useGroupSort,
    useFieldSetting,
    useAutoMerge,
    useOprCol,
} from "@platform/table-core";
import FormList from './FormList';
import ExpandedForm from './ExpandedForm';
import { Tooltip, ScrollElement, WithKeyboardChange, WithAutoFocus } from '@platform/base';
import { isValidType } from '@platform/template';
import { getSafeRandom, getSysFieldid, getGlobalStorage, getMultiLang, getTopWindow, microAppUtil, isNewVisual } from '@platform/api';
import {
    handleBeforeEvent,
    handleAfterEvent,
    splitPks,
    isEmptyValue,
    getRowHeight,
    setAutoWrap,
} from '../../utils';

import { ROW_STATUS, getScrollBarSize } from '../../../utils'
import { getAutoWrap } from '../../../libs/useContextMenu';

const { TableWithKeyboardChange } = WithKeyboardChange;
const { TableWithAutoFocus } = WithAutoFocus;

let hookExtensions = [
    TableWithAutoFocus,
    TableWithKeyboardChange,
    connect,
    useHotKey,
    useAutoMerge,
    useStore,
    useResize,
    useIndex,
    useCheckbox,
    useExpandRow,
    useRowClick,
    useTreeTable,
    useSort,
    useCellSelect,
    useDOM,
    useCardTable,
    usePagination,
    useCache,
    useFilter,
    useLocation,
    useContextMenu,
    useGroupSort,
    useFieldSetting,
    useStyle,
    // useCopy,
    useErrorTips,
    useOprCol,
];

const Table = hookExtensions.reduceRight((Table, HOC) => HOC(Table), BaseTable);

const generateKey = () => String(new Date().getTime()).slice(-5) + getSafeRandom().toString(12);

const scrollBarSize = getScrollBarSize();

class CardTable extends Component {
    locale = null; // 存一份多语言json
    componentDidMount() {
        let { stores, relation } = this.props;
        getMultiLang({
            moduleId: "page_table",
            callback: json => {
                this.locale = json;
                relation.dependence.forEach(tableId => {
                    stores[tableId].setLocale(this.locale);
                });
            },
        });
    }

    componentDidUpdate() {
        let { stores, id, relation } = this.props,
            { open, tableId } = stores[id].getCardTableModalInfo();


        if (!!open && !stores[tableId].getRows()?.length) {
            stores[id].closeCardTableModal();
        }

        relation.dependence.forEach(tableId => {
            if (!Object.keys(stores[tableId].getLocale()).length && this.locale) {
                stores[tableId].setLocale(this.locale);
            }
        });
    }

    render() {
        let { _tables, relation } = this.props,
            { tables } = relation;

        _tables = _tables ?? tables

        return _tables.map((moduleId, index) => {
            return <SingleTable {...this.props} _moduleId={moduleId} _index={index} _tablesCount={_tables.length} key={moduleId}></SingleTable>;
        });
    }
}

// 主要作用为：创建UI、分发Store
class SingleTable extends Component {
    constructor(props) {
        super(props);
        this.state = { formLayout: { column: 1, width: 100 } };
        this.microAppContainer = microAppUtil.getDocument(this.props._runContext);
    }
    scrollBarStatus = false;
    cardTableTarget = null;

    componentDidMount() {
        let { stores, relation, _moduleId } = this.props;
        relation.tabs[_moduleId].forEach(moduleId => {
            stores[moduleId].setRef(this);
        });
    }

    componentDidUpdate() {
        let { stores, relation, _moduleId, pageScope } = this.props;
        relation.tabs[_moduleId].forEach(moduleId => {
            stores[moduleId].setRef(this);
        });

        // 客开的旧版本钩子兼容,没有客开时不会执行客开方法
        let store = stores[_moduleId];
        let data = store?.getRows() || [];
    }

    render() {
        let { _tablesCount, _moduleId, _index, configs, id, stores } = this.props,
            { maxZIndex = 270, getPopupContainer } = configs[id];

        this.tablesCount = _tablesCount;

        let store = stores[_moduleId],
            isMax = store.getMaxStatus();

        let container = typeof getPopupContainer === 'function' ? getPopupContainer() : this.microAppContainer,
            position = [window, document, document.body].includes(container) ? 'fixed' : 'absolute';

        if (isMax) {
            // 最大化状态
            return createPortal(
                <div className='max-cardTable-backdrop' style={{ zIndex: maxZIndex, position }}>
                    <div className='max-cardTable flex-container' style={{ position }}>
                        {this.renderCardTable(_moduleId, _index)}
                    </div>
                </div>
                , container);
        } else {
            // 标准状态
            return this.renderCardTable(_moduleId, _index);
        }
    }

    preHtmlOverflow = '';

    overflowHtml(isHidden = true) {
        const html = document.getElementsByTagName('html')[0];
        if (isHidden) {
            this.preHtmlOverflow = getComputedStyle(html).overflow;
            html.style.overflow = 'hidden';
            return;
        }
        html.style.overflow = this.preHtmlOverflow;
    }

    componentWillUnmount() {
        let { stores, relation, _moduleId } = this.props;
        relation.tabs[_moduleId].forEach(moduleId => {
            stores[moduleId].removeRef(this);
        });
        // Tips: 点击全屏按钮的时候html标签会增加删除overflow: hidden; 避免全屏状态下切换路由导致页面无法滚动。
        this.overflowHtml(false);
    }

    renderCardTable = (moduleId, index) => {
        let { stores, configs } = this.props;

        let store = stores[moduleId],
            activeTab = store.getActiveTab(),
            tabs = this.getTabs(moduleId);

        if (!activeTab || !stores[activeTab]) {
            // render先于didMount执行，此时还没有setRef，无法更新，故手动设置一下
            // NCC-292954
            if (!store.getRef()) {
                store.setRef(this);
            }
            if (tabs[0]) {
                store.setActiveTab(tabs[0]);
                activeTab = tabs[0];
            } else {
                return null;
            }
        }

        let { fieldid, hideTableTabs, customTabContent, adaptionHeight, layoutMode } = configs[moduleId] || {},
            isExpand = store.getTableExpand(),
            isExpandAll = stores[activeTab].getExpandAllStatus(),
            status = stores[activeTab].getStatus(),
            isMax = store.getMaxStatus();

        let header = this.renderCardTableHeader(moduleId);

        return <ResizeObserver
            onResize={(_, target) => {
                // 这里会触发更新，所以overflow存在this上了
                this.cardTableTarget = target;
                this.calcWidth(_.width);
            }}
        >
            <div
                className={classnames({
                    'flex-container': adaptionHeight || isMax,
                    'vertical-mode': layoutMode === "vertical",
                    'nc-bill-single-table': this.tablesCount === 1,
                    'nc-bill-tableTab-area': this.tablesCount === 2 && index === 1,
                    'nc-bill-table-area': this.tablesCount > 2 || (this.tablesCount === 2 && index === 0),
                })}
                style={{ marginTop: layoutMode === "vertical" ? 4 : 0 }}
            >
                <div
                    key={moduleId}
                    className={classnames("card-table-wrapper", {
                        'flex-container': isExpand,
                        "new-visual": isNewVisual(),
                    })}
                    moduleId={moduleId}
                    fieldid={getSysFieldid(`${fieldid || moduleId}_table-area`)}
                >
                    {/* 头部：页签、按钮区 */}
                    {!hideTableTabs && header}
                    {typeof customTabContent?.[activeTab] === 'function' ? customTabContent[activeTab](activeTab) : (hideTableTabs || header) && <>
                        {isExpand ? (
                            isExpandAll && status === 'browse' ?
                                // 表单视图
                                this.renderFormList(moduleId) :
                                // 表格视图
                                this.renderCardTableBody(moduleId, index)
                            // <TableBody {...this.props} _moduleId={moduleId} _index={index} />
                        ) : this.renderPlaceholderTable()}
                    </>}
                </div>
            </div>
        </ResizeObserver>;
    }

    getTabs = (moduleId) => {
        let { relation, stores, pageScope } = this.props,
            { tabs: tabsInfo } = relation,
            tabs = tabsInfo[moduleId];

        tabs = tabs.filter(tab =>
            stores[tab].getAreaVisible() && pageScope.state.meta?.[tab]?.areaVisible !== false,
        );

        return tabs;
    }

    renderCardTableHeader = moduleId => {
        let { relation, stores, configs = {}, pageScope } = this.props;
        let { home } = relation;

        let store = stores[moduleId],
            activeTab = store.getActiveTab() || moduleId,
            isExpand = store.getTableExpand(),
            isExpandAll = stores[activeTab].getExpandAllStatus(),
            status = stores[activeTab].getStatus(),
            isMax = store.getMaxStatus(),
            config = configs[activeTab] || {},
            locale = store.getLocale();

        let {
            tableHead,
            hideMaxBtn,
            hideSwitch,
            customTabContent, // 自定义页签内容
            showToggleAngle = true, // 是否显示展开收起图标
            tableHeadLeft, // header左侧的扩展区域
            showCheck, // 是否显示已选数量，感觉和下面重复了
            showSelectedNum = true, // 是否显示已选数量，感觉和上面重复了
            showTotalNum = true, // 是否显示合计数目
            initShowTotalNum = true, // 是否始终显示数量（为0时不显示）
            showEditIcon = true, // 是否显示编辑图标（小铅笔）
            hideBrowseCheckbox,
        } = config;

        let isSingleTab = false,
            tabs = this.getTabs(moduleId);

        showCheck = !hideBrowseCheckbox ? showCheck : status === "browse" ? !hideBrowseCheckbox[activeTab] : showCheck;

        // 如果所有页签都不可见，则整个header区域都不显示。以前的代码是这样写的
        if (!tabs.length) {
            return null;
        }

        if (tabs.length <= 1) {
            isSingleTab = true;
        }

        return <div className="card-table-tabs" fieldid={getSysFieldid("tabs-area")}>
            {showToggleAngle && <span
                className={classnames("card-table-expand-icon", { expand: isExpand })}
                onClick={() => {
                    stores[moduleId].setTableExpand(!isExpand);
                    tabs.forEach(tab => configs[tab]?.onHeadAngleToggle?.({ ...pageScope.props, ...pageScope.output }, home[tab], !isExpand, tab)); //和toggleCardTable中回调的参数顺序保持一致
                    this.forceUpdate();
                }}
            >
                <span className='iconfont icon-right'></span>
            </span>}
            {tabs.map((tabId, index) => {
                let tabStore = stores[tabId],
                    leafKeys = tabStore.getVisibleLeafColumnKeys(),
                    hasEditableField = !isSingleTab && showEditIcon && status === 'edit' && leafKeys.some(
                        key => !tabStore.getColumnProps(key, 'disabled'),
                    ),
                    hasInvalidField = !!tabStore.getValidateInfo()?.size,
                    rowsCount = tabStore.getVisibleRowKeys().length;

                // 将createCardTable的参数存入各自的store
                // 主要是取pkname
                tabStore.setConfig(configs[tabId]);
                tabStore.setPageScope(pageScope);

                let meta = pageScope.state.meta?.[tabId];

                if (!meta) { return null; }

                const handleTabClick = () => {
                    if (activeTab !== tabId) {
                        // 找到当前组的第一个页签（relation.home）设置activeTab
                        stores[moduleId].setActiveTab(tabId);
                        this.forceUpdate();
                    }
                }

                return <ScrollElement key={tabId} name={tabId} className="tab-wrapper">
                    {index > 0 && <span className="card-table-tab-sep">|</span>}
                    <span
                        tabIndex={tabId === activeTab ? 0 : -1}
                        tabId={tabId}
                        onClick={() => {
                            handleTabClick();
                        }}
                        onFocus={() => {
                            // 方式和click冲突触发两遍
                            setTimeout(() => {
                                handleTabClick();
                            }, 0);
                        }}
                        onKeyDown={e => {
                            let key = e.keyCode || e.charCode || e.which;
                            switch (key) {
                                case 9: // tab
                                case 13: // enter
                                    let visibleRows = tabStore.getVisibleRows(),
                                        visibleLeafColumnKeys = tabStore.getVisibleLeafColumnKeys().filter(
                                            key => isValidType(tabStore.getColumnProps(key, 'itemtype')) && !tabStore.getColumnProps(key, 'disabled'),
                                        );

                                    if (e.shiftKey) {
                                        // 跳到上一个区域的最后一个可编辑字段

                                    } else {
                                        // 跳到当前区域的第一个可编辑字段
                                        let firstRow = visibleRows?.[0]?.key,
                                            firstCol = visibleLeafColumnKeys[0];

                                        if (firstRow && firstCol) {
                                            e.preventDefault();
                                            tabStore.focusOnCell(firstRow, firstCol);
                                        }
                                    }
                                    break;
                                case 32: // space
                                    stores[moduleId].setTableExpand(!isExpand);
                                    this.forceUpdate();
                                    break;
                                case 37: // left
                                    if (e.shiftKey) {
                                        // 跳到上个页签
                                        let activeTab = tabs[(index + tabs.length - 1) % tabs.length];
                                        stores[moduleId].setActiveTab(activeTab);
                                        document.querySelector(`.card-table-tab[tabid=${activeTab}]`)?.focus();
                                    }
                                    break;
                                case 39: // right
                                    if (e.shiftKey) {
                                        // 跳到下个页签
                                        let activeTab = tabs[(index + 1) % tabs.length];
                                        stores[moduleId].setActiveTab(activeTab);
                                        document.querySelector(`.card-table-tab[tabid=${activeTab}]`)?.focus();
                                    }
                                    break;
                                default:
                                    break;
                            }
                        }}
                        className={classnames('card-table-tab', { 'active-tab': !isSingleTab && tabId === activeTab })}
                    >
                        {/* 编辑图标 */}
                        {hasEditableField && <i className='icon iconfont icon-yebj edit-icon'></i>}
                        {/* 页面名称 */}
                        {meta.name}
                        {/* 表格条数 */}
                        {showTotalNum && (rowsCount || initShowTotalNum) && <>
                            {" "}({rowsCount})
                        </>}
                        {/* 错误图标 */}
                        {hasInvalidField && <i className="iconfont icon-djgantanhao error-icon"></i>}
                    </span>
                </ScrollElement>;
            })}
            <div className='tab-wrapper'>
                {showCheck && showSelectedNum && locale?.['table0034'] && <span className="selected-count">
                    <>
                        {`${locale['table0034'] /*已选*/} ${stores[activeTab].getCheckedKeys().size} ${locale['table0035'] /*条*/}`}
                    </>
                </span>
                }
            </div>
            <div className='header-extend tab-wrapper'>
                {tableHeadLeft?.()}
            </div>
            {isExpand && <div className="button-area">
                {/* 业务肩部按钮 */}
                {tableHead?.()}
                {/*全部展开按钮*/}
                {status === "browse" && !(typeof hideSwitch === 'function' && !hideSwitch()) && <Tooltip
                    placement={isMax ? 'bottom' : 'top'}
                    overlay={isExpandAll ? locale['table-view-list' /*列表显示*/] : locale['table-view-card' /*卡片显示*/]}
                    delayHide={1}
                    delayShow={1000}
                >
                    <span
                        className={classnames(
                            "icon iconfont head-icon",
                            {
                                "icon-shituqiehuan": isExpandAll,
                                "icon-shituliebiaoqiehuan": !isExpandAll,
                            },
                        )}
                        onClick={() => {
                            stores[activeTab].setExpandAllStatus(!isExpandAll);
                        }}
                    />
                </Tooltip>}
                {/*最大化按钮*/}
                {!hideMaxBtn && (
                    <Tooltip
                        placement={isMax ? 'bottom' : 'top'}
                        overlay={isMax ? locale['table-zoom-normal' /*还原*/] : locale['table-zoom-max' /*最大化*/]}
                        delayHide={1}
                        delayShow={1000}
                    >
                        <span
                            className={classnames(
                                "icon iconfont head-icon",
                                {
                                    "icon-zuixiaohua": isMax,
                                    "icon-zuidahua": !isMax,
                                },
                            )}
                            onClick={() => {
                                this.overflowHtml(!isMax);
                                store.setMaxStatus(!isMax);
                            }}
                        />
                    </Tooltip>
                )}
            </div>}
        </div>;
    }

    renderCardTableBody = (moduleId, index) => {
        let { id, relation, stores, configs = {}, pageScope, ...others } = this.props;
        let { expand, dataSource, share } = relation;
        let { formLayout } = this.state;

        let store = stores[moduleId],
            activeTab = store.getActiveTab() || moduleId,
            config = configs[activeTab] || {},
            expandedFormId = expand[activeTab],
            meta = pageScope.state.meta,
            { expandFormWithMeta = false,
                setCellClass,
                setHeadClass,
                onAfterEvent,
                onBeforeEvent,
                preventAfterEventWhenBrowse,
                onCellKeyDown,
                onRowClick,
                onSelected,
                onSelectedAll,
                onBatchSelected, // 框选事件
            } = stores[activeTab].getConfig() ?? {},
            formItems = meta?.[expandedFormId]?.items.filter(col => expandFormWithMeta ? col.visible : ((col.visible || stores[activeTab].getColumnProps(col.attrcode, 'visible')) && isValidType(col.itemtype))) ?? [],//按模板来 or 模板和表体行共同控制
            isMax = store.getMaxStatus(),
            tabs = this.getTabs(moduleId);

        let {
            showIndex,
            showCheck,
            // onAfterEvent,
            // onBeforeEvent,
            onRowDoubleClick,
            onBatchChange, // 批改事件
            selectedChange,
            showPagination,
            handlePageInfoChange,
            alloweFilter: enableFilter = true,
            high_selectable,
            hideRightMenu = false,
            cancelCustomRightMenu = false,
            showWidthAndHeightConfig = true,
            extendRightMenu,
            extendRightMenuPlacement,
            handleFilterCallBack,
            handleResetCallBack,
            sort,
            isAddRow,
            addRowDefaultValue,
            addRowCallback,
            hiddenLeftItem,
            noColSetClass,
            colSetCallback,
            showTotal,
            rightMenuClick,
            inputChange,
            // setCellClass,
            fieldid,
            footer,
            allowTotalRows,
            noTotalRows,
            noTotalCol,
            height,
            contextMenuDisabled = [],
            adaptionHeight,
            addBlankCol,
            rowClassList,
            fixFooter,
            fixHeader,
            scrollContainer = () => {
                if (document.querySelector('#dragHeightCom_bottom')) { //卡片页业务助手左右分栏情况
                    return document.querySelector('#dragHeightCom_bottom');
                } else {
                    return window;
                }
            },
            columnWidthConfig = true,
            locatable = true,
            sortable = true,
            lazyload = true,
            virtualHead,
            rowProps,
            components,
            onTableCellClick,
            checkboxMergeInfo,
            indexMergeInfo,
            mergeCell,
            autoMergeCell,
            onScroll,
            renderEmptyArea,
            hiddenDelRow,
            hiddenFieldSetting,
            customSetting = {}, //个性化设置参数
            customIndexParams = {}, //领域传来的参数
            onContextMenu,
            enterDirection = 'right',
            rowHeight,
        } = config,
            useVirtual = true;

        if (hideRightMenu) {
            cancelCustomRightMenu = true;
        }

        //初始化
        if (config.hasOwnProperty('multipleRowCell')) {
            setAutoWrap(stores[activeTab], activeTab, config.multipleRowCell);
        }

        if (this.tablesCount === 2) {
            adaptionHeight = index === 0 ? false : true;
        } else if (this.tablesCount > 2) {
            adaptionHeight = false;
        }
        if (isMax) {
            adaptionHeight = true;
        }

        let { formId } = stores[id].getCardTableModalInfo(),
            rowsHeight = stores[activeTab].getRowsHeight();

        let ncc_automated = getGlobalStorage('localStorage', '_ncc_automated_');
        if (ncc_automated == '1') {
            // 自动化测试开关打开
            useVirtual = false;
        }

        if (stores[activeTab].getMaxStatus()) {
            scrollContainer = undefined;
        }

        let fontClass = window?.nccFontSize || (window && getTopWindow()?.nccFontSize) || getGlobalStorage('localStorage', 'nccFontSize') || '';

        return tabs.map((tab) => {
            if (tab === activeTab) {
                return <Table
                    mergeCell={!!mergeCell || autoMergeCell}
                    {...others}
                    components={components}
                    onScroll={onScroll}
                    _keyboardProps={this.props}
                    rowProps={row => {
                        let className = '',
                            dataIndex = row.dataIndex;

                        if (Array.isArray(rowClassList)) {
                            className = rowClassList.filter(({ index }) => {
                                if (!Array.isArray(index)) {
                                    index = [index];
                                }
                                return index.includes(dataIndex);
                            }).map(e => e.className).join(' ');
                        } else if (typeof rowClassList === 'function') {
                            className = rowClassList(row, dataIndex);
                        } else {
                            className = rowClassList;
                        }

                        return {
                            className,
                            onClick: e => onRowClick?.({ ...pageScope.props, ...pageScope.output }, activeTab, row, stores[activeTab].getRowData(row.key).dataIndex, e),
                            onDoubleClick: onRowDoubleClick && (e => onRowDoubleClick?.({ ...pageScope.props, ...pageScope.output }, row, stores[activeTab].getRowData(row.key).dataIndex, e)),
                            ...rowProps?.(),
                        }
                    }}
                    id={activeTab}
                    currentId={activeTab}
                    autoRowHeight={getAutoWrap(store, activeTab)}
                    key={activeTab}
                    totalConfig={{ enable: showTotal }}
                    rowKey={row => row.key}
                    store={stores[activeTab]}
                    addBlankCol={addBlankCol}
                    flex={adaptionHeight}
                    fixFooter={fixFooter}
                    fixHeader={fixHeader}
                    scrollContainer={scrollContainer}
                    renderEmptyArea={renderEmptyArea}
                    bodyStyle={{
                        minHeight: 60, // 2行
                        maxHeight: adaptionHeight ? 'none' : (height || getRowHeight() * 10),
                        height: height || 'auto',
                    }}
                    setHeadClass={setHeadClass}
                    rowHeight={rowHeight || getRowHeight()}
                    defaultRenderStatus='edit'
                    allowTotalRows={allowTotalRows}
                    noTotalRows={noTotalRows}
                    noTotalCol={noTotalCol}
                    renderItems={pageScope.state.renderItem?.table?.[activeTab] || {}}
                    virtualConfig={{ x: useVirtual, y: !!lazyload, header: virtualHead }}
                    expandConfig={{
                        enable: true,
                        height: (Object.values(rowsHeight).length ? Object.values(rowsHeight)[1] : 150) ?? 150,
                        fixed: true,
                        render: row => {
                            return <ResizeObserver
                                onResize={({ height }) => {
                                    // 这里的rowKey和TableBody里的getExpandedRowKey保持统一
                                    rowsHeight[`${row.key}-expanded`] = height;
                                    stores[activeTab].setRowsHeight(rowsHeight);
                                }}
                            >
                                <ExpandedForm
                                    layout={formLayout}
                                    items={formItems}
                                    values={row.values}
                                    store={stores[activeTab]}
                                    rowKey={row.key}
                                    formStore={stores[expandedFormId]}
                                ></ExpandedForm>
                            </ResizeObserver>;
                        },
                    }}
                    indexConfig={{ enable: showIndex, mergeInfo: indexMergeInfo, customIndexParams }}
                    checkboxConfig={{
                        enable: showCheck,
                        more: showPagination,
                        mergeInfo: checkboxMergeInfo,
                        onCheck: (rowKey, { isChecked, checkedKeys, lastCheckedKeys }) => {
                            let visibleKeysLength = stores[activeTab].getVisibleRowKeys()?.length || 0;
                            relation.share[activeTab].forEach(tabId => {
                                stores[tabId].setCheckedKeys(checkedKeys);
                                if (stores[tabId].getGridTableId()) {
                                    pageScope.cardTableDataSource[stores[tabId].getGridTableId()].setCheckedKeys(checkedKeys);
                                }
                            });

                            if (rowKey) { //单行改变
                                let row = stores[activeTab].getRowData(rowKey);
                                if (isChecked) {
                                    stores[activeTab].setClickedRow(row.key);
                                } else {
                                    stores[activeTab].setClickedRow('');
                                }
                                onSelected?.(
                                    { ...pageScope.props, ...pageScope.output },
                                    activeTab,
                                    row,
                                    row.dataIndex,
                                    isChecked,
                                    //TODO isUnique
                                );
                            } else {
                                onSelectedAll?.(
                                    { ...pageScope.props, ...pageScope.output },
                                    activeTab,
                                    !!checkedKeys?.size,
                                    visibleKeysLength,
                                );
                            }
                            selectedChange?.({ ...pageScope.props, ...pageScope.output }, activeTab, checkedKeys?.size || 0, lastCheckedKeys?.size || 0);
                        },
                    }}
                    paginationConfig={{
                        enable: showPagination,
                        paginationExtendRender: config.paginationExtendRender,
                        onChange: ({ pageIndex, pageSize }) => {
                            let allPks = stores[activeTab].getAllRowsInfo()?.allpks;
                            let pks = splitPks(allPks, pageIndex, pageSize);
                            handlePageInfoChange?.({ ...pageScope.props, ...pageScope.output }, config, pks, stores[activeTab].getPaginationInfo()?.total, { tableId: activeTab });
                        },
                    }}
                    fieldSettingConfig={{ enable: !hiddenFieldSetting }}
                    filterConfig={{
                        enable: enableFilter,
                        title: pageScope.state.meta?.[activeTab]?.name || '',
                        onFilter: handleFilterCallBack,
                        onReset: handleResetCallBack,
                    }}
                    sortConfig={{
                        enable: sortable,
                        onSort: orderMap => {
                            // share[activeTab].forEach(shareId => {
                            //     if (shareId === activeTab) { return; };

                            //     stores[shareId].setSortInfo(orderMap);
                            // });
                            // let sortInfo = [...orderMap].map(([colKey, order], index) => {
                            //     return {
                            //         order: order === 1 ? "ascend" : "descend",
                            //         field: colKey,
                            //         orderNum: index,
                            //     };
                            // });
                            // sort?.sortFun?.(sortInfo, stores[activeTab].getVisibleRows());
                        },
                    }}
                    locationConfig={{ enable: locatable }}
                    cellSelectConfig={{
                        enable: high_selectable?.enable ?? true,
                        batchSelectEnable: !!onBatchChange,
                        onSelect: (selectedCell, { startCell, endCell, batchSelect }) => {
                            console.log('框选', selectedCell);

                            if (!startCell || !endCell || batchSelect) {
                                return;
                            }

                            if (onBatchSelected) {
                                /**
                                 * 传了这个参数，才勾选前面的行。以前的逻辑是这样的
                                 * 如果选中的是单行，走onRowClick逻辑
                                 * 否则这里设置选中
                                 */
                                share[activeTab].forEach(shareId => {
                                    let selectedRows = stores[shareId].getCheckedKeys();
                                    stores[shareId].setCheckedKeys(new Set([...selectedCell.keys()].concat(...selectedRows)));
                                }
                                );
                                // startCell.row.key !== endCell.row.key && stores[activeTab].setCheckedKeys(new Set([...selectedCell.keys()]));
                                // 单行onRowClick走不到这里，cellSelect那里已经阻断了

                                onBatchSelected(
                                    {
                                        ...pageScope.props,
                                        ...pageScope.output,
                                    },
                                    activeTab,
                                    [...selectedCell].map(([rowKey]) => stores[activeTab].getRowData(rowKey)),
                                );
                            }
                        },
                        onChange: (selectedRows, { column, row, oldValues, texts, values }) => {
                            console.log('批改', selectedRows, column, row);
                            let baseRow = selectedRows[0];
                            // if(texts[0] === oldValues[0]?.value){
                            //     selectedRows = selectedRows.slice(1, selectedRows.length);
                            //     texts = texts.slice(1, texts.length);
                            //     oldValues = oldValues.slice(1, oldValues.length);
                            // }

                            let colKey = column.key;

                            onBatchChange?.({
                                allData: stores[activeTab].getRows(),
                                areaCode: activeTab,
                                changedrows: selectedRows.map((row, index) => {
                                    return {
                                        newvalue: { value: texts[index] },
                                        oldvalue: oldValues[index],
                                        rowid: row.key,
                                    };
                                }),
                                column,
                                row,
                                currentIndex: baseRow.dataIndex,
                                newValue: selectedRows.map((row, index) => {
                                    row = {
                                        ...row,
                                        values: {
                                            ...row.values,
                                            [colKey]: { value: texts[index] }
                                        }
                                    }
                                    return row;
                                }),
                                pasteData: texts.map((e) => typeof e === 'string' ? e.trim() : e),
                                values,
                            });

                            // selectedRows.forEach(row => {
                            //     stores[activeTab].setCellValue(row.key, column.key, { value: row.values[colKey] });
                            // });
                        },
                        exclude: high_selectable?.disabledColumn ?? [],
                    }}
                    onBeforeEvent={({ value, event, colKey, rowKey }) => {
                        console.log('CardTable表体编辑前事件', value, event, colKey, rowKey);

                        let row = stores[activeTab].getRowData(rowKey),
                            column = stores[activeTab].getColumnProps(colKey);

                        stores[activeTab].setCache('oldBatchData', { colKey, dataIndex: row.dataIndex });
                        stores[activeTab].setClickedRow(rowKey);
                        stores[activeTab].setCellProps(rowKey, colKey, { invalid: false });
                        stores[activeTab].clearCellValidate(rowKey, colKey);

                        return handleBeforeEvent.call(pageScope, {
                            row,
                            column,
                            value,
                            moduleId: dataSource[activeTab],
                            callback: () => {
                                return onBeforeEvent?.(
                                    { ...pageScope.props, ...pageScope.output },
                                    dataSource[activeTab],
                                    column.attrcode,
                                    value,
                                    row.dataIndex,
                                    stores[activeTab].getInstance()._formatOutputRow(row),
                                    'line',
                                );
                            },
                            type: 'onCardTableBeforeEvent',
                        });
                    }}
                    onAfterEvent={({ value, oldValue, componentValue, event, colKey, rowKey }) => {
                        console.log('CardTable表体编辑后事件', value, oldValue, componentValue, event, colKey, rowKey);

                        let row = stores[activeTab].getRowData(rowKey),
                            column = stores[activeTab].getColumnProps(colKey);

                        // Item统一处理trim
                        // typeof value?.value === 'string' && (value.value = value.value.trim());

                        if (column.itemtype === "residtxt") {
                            // 多语文本value格式不一样，要特殊处理一下
                            [...Object.entries(value), [colKey, value[colKey]]].forEach(([subCode, subValue]) => {
                                // 同步单来源的值
                                share[activeTab].forEach(shareId => {
                                    if (shareId === activeTab) { return; };

                                    stores[shareId].setCellValue(rowKey, subCode, { value: subValue });
                                });

                                // 同步侧拉的值
                                pageScope.formDataSource[formId]?.setItemValue(subCode, subValue);
                            });
                        } else {
                            // 同步单来源的值
                            share[activeTab].forEach(shareId => {
                                if (shareId === activeTab) { return; };

                                stores[shareId].setCellValue(rowKey, colKey, { value });
                            });

                            // 同步侧拉的值
                            pageScope.formDataSource[formId]?.setItemValue(colKey, value);
                        }

                        // 触发业务编辑后
                        handleAfterEvent.call(pageScope, {
                            row: stores[activeTab].getRowData(rowKey),
                            column: stores[activeTab].getColumnProps(colKey),
                            value,
                            oldValue,
                            componentValue,
                            moduleId: dataSource[activeTab],
                            activeTab,
                            callback: ({ changedRows }) => {
                                // handleAfterEvent里更新了数据，这里要同步
                                row = stores[activeTab].getRowData(row.key);
                                if (preventAfterEventWhenBrowse && stores[activeTab].getStatus == "browse") {
                                    return;
                                }
                                return onAfterEvent?.(
                                    {
                                        ...pageScope.props,
                                        ...pageScope.output,
                                    },
                                    dataSource[activeTab],
                                    column.attrcode,
                                    column.itemtype === 'refer' || column.itemtype === 'residtxt' ? componentValue : value.value,
                                    changedRows,
                                    row.dataIndex,
                                    stores[activeTab].getInstance()._formatOutputRow(row),
                                    'line',
                                    'change',
                                    stores[activeTab].getVisibleRows(),
                                    event,
                                );
                            },
                            type: 'onCardTableAfterEvent',
                            store: stores[activeTab],
                        });

                        let visibleRows = stores[activeTab].getVisibleRows();

                        // 自动增行的处理 是最后一行 & 编辑态
                        if (isAddRow && (value?.value || column.itemtype === "residtxt") && row.viewIndex === visibleRows.length - 1 && stores[activeTab].getStatus() === 'edit') {
                            // 不知道在干嘛 但是逻辑应该没错
                            let validateValue = value.value;
                            let switchBrowser = column.itemtype === 'switch_browse' && column.disabled;

                            if (column.itemtype === 'residtxt') {
                                validateValue = value;
                            } else if (column.itemtype === 'refer') {
                                validateValue = componentValue;
                            }

                            if (!switchBrowser && !isEmptyValue(validateValue, column.itemtype)) {
                                let key = generateKey();
                                if (addRowDefaultValue instanceof Promise) {
                                    addRowDefaultValue.then(rowValues => {
                                        relation.share[activeTab].forEach(tabId => {
                                            stores[tabId].addRow(visibleRows.length, { values: rowValues, key }, { autoFocus: false });
                                        });
                                        visibleRows = stores[activeTab].getVisibleRows();
                                        addRowCallback?.(stores[activeTab].getVisibleRows()[visibleRows.length - 1]);
                                    });
                                } else {
                                    addRowDefaultValue = typeof addRowDefaultValue === 'function' ? (addRowDefaultValue() ?? {}) : JSON.parse(JSON.stringify(addRowDefaultValue || {}));

                                    relation.share[activeTab].forEach(tabId => {
                                        stores[tabId].addRow(visibleRows.length, { values: addRowDefaultValue, key }, { autoFocus: false });
                                    });
                                    visibleRows = stores[activeTab].getVisibleRows();
                                    addRowCallback?.(stores[activeTab].getVisibleRows()[visibleRows.length - 1]);
                                }
                            }
                        }
                    }}
                    onValueChange={(rowKey, { attrcode, value }) => {
                        // 编辑中change回调 参考原代码
                        let column = stores[activeTab].getColumnProps(attrcode),
                            row = stores[activeTab].getRowData(rowKey);
                        // 为郭祉琪加的针对input类型时,这两个字段的特殊校验,换算率
                        if (column.itemtype === 'input' && ['vchangerate', 'vqtunitrate'].includes(column.attrcode)) {
                            // eslint-disable-next-line no-useless-escape
                            let reg = /^[0-9\/\.]+$/;
                            if (value && !reg.test(value)) { //只能输入数字和/
                                return;
                            }
                        }
                        //胡杰李忠博加
                        if (typeof inputChange === 'function') {
                            // 转换一下index
                            inputChange({ index: row.dataIndex, item: column, valueChange: value.value });
                        }

                        row.status === ROW_STATUS.ORIGIN && (row.status = ROW_STATUS.EDIT);

                        // stores[activeTab].setRowData({ ...row, value: { ...row.values, [attrcode]: value }});
                        // NCC-298945 setRowData会forceUpdate
                        stores[activeTab].setCellValue(row.key, attrcode, { value });
                    }}
                    contextMenuConfig={{
                        enable: !hideRightMenu && (high_selectable?.enable ?? true) && (high_selectable?.selectCell ?? true),
                        hiddenDelRow,
                        extendRightMenu,
                        extendRightMenuPlacement,
                        rightMenuClick,
                        cancelCustomRightMenu,
                        showWidthAndHeightConfig,
                        hiddenLeftItem,
                        columnWidthConfig,
                        disableArea: (rowKey, colKey) => {
                            if (!noColSetClass) { return false; }
                            let cellClass = stores[activeTab].getCellProps(rowKey, colKey, 'className');
                            return cellClass?.includes(noColSetClass);
                        },
                        onVisibleChange: visible => {
                            if (visible) {
                                colSetCallback?.();
                            }
                        },
                        onClick: (key) => {
                            pageScope.forceUpdate();
                            onContextMenu?.(key);
                        },
                        disabled: new Set(contextMenuDisabled),
                    }}
                    cellProps={(rowInfo, colInfo) => {
                        let props = {};
                        if (typeof onCellKeyDown === 'function') {
                            props.onKeyDown = (e) => {
                                onCellKeyDown.call(pageScope, { ...pageScope.props, ...pageScope.output }, activeTab, rowInfo.row, rowInfo.rowIndex/** viewIndex */, colInfo.col.key, colInfo.col, e, 'table');
                            }
                        }
                        if (typeof onTableCellClick === 'function' && stores[activeTab].getStatus() === "browse") {
                            props.onClick = e => {
                                onTableCellClick(rowInfo.row, rowInfo.rowIndex/** viewIndex */, colInfo.col.key, colInfo.col, e); //TODO 传给业务的可能需要转成dataIndex
                            };
                        }
                        if (typeof setCellClass === 'function') {
                            props.className = setCellClass(rowInfo.rowIndex/** viewIndex */, rowInfo.row, colInfo.col.key);
                        }
                        return props;
                    }}
                    fieldid={fieldid}
                    footer={footer}
                    fontClass={fontClass}
                    _onScrollBarStatusChange={status => {
                        this.scrollBarStatus = status;
                        this.calcWidth(this.cardTableTarget?.querySelector('.base-table-next')?.clientWidth);
                    }}
                    pageScope={pageScope}
                    customSetting={customSetting}
                    hotkeyConfig={{
                        enterDirection,
                    }}
                />;
            } else {
                return this.renderPlaceholderTable();
            }
        })
    }

    renderPlaceholderTable = () => {
        return <div style={{ display: 'none' }} className='table-with-auto-focus'></div>;
    }

    renderFormList = (moduleId, index) => {
        let { stores, relation, configs, pageScope } = this.props,
            { expand } = relation,
            { formLayout, width } = this.state,
            store = stores[moduleId],
            { adaptionHeight } = configs[moduleId],
            activeTab = store.getActiveTab() || moduleId,
            isMax = store.getMaxStatus(),
            expandedFormId = expand[activeTab],
            meta = pageScope.state.meta,
            formItems = meta?.[expandedFormId]?.items.filter(col => (col.visible || stores[activeTab].getColumnProps(col.attrcode, 'visible')) && isValidType(col.itemtype)) ?? [];

        if (isMax) {
            adaptionHeight = true;
        } else if (this.tablesCount === 2) {
            adaptionHeight = index === 0 ? false : true;
        } else if (this.tablesCount > 2) {
            adaptionHeight = false;
        }

        return <FormList
            formLayout={formLayout}
            {...this.props}
            moduleId={activeTab}
            store={stores[activeTab]}
            isMax={isMax}
            formItems={formItems}
            width={width}
            flex={adaptionHeight}
            formId={expandedFormId}
        />;
    }

    calcWidth = width => {
        if (!width) { return; };
        if (this.scrollBarStatus) {
            width -= scrollBarSize;
        }
        // 列数需要根据组件宽度得到，而以下数值是UE给的分辨率
        // 组件宽度 + 32 = 分辨率
        // 加30缓冲区（如：滚动条）
        let screenWidth = width + 32 + 30;

        let formLayout = {};
        if (screenWidth < 600) {
            formLayout = { width: 100, column: 1 };
        }
        if (screenWidth >= 600 && screenWidth <= 1024) {
            formLayout = { width: 50, column: 2 };
        }
        if (screenWidth >= 1025 && screenWidth <= 1439) {
            formLayout = { width: 100 / 3, column: 3 };
        }
        if (screenWidth >= 1440 && screenWidth <= 1919) {
            formLayout = { width: 25, column: 4 };
        }
        if (screenWidth >= 1920) {
            formLayout = { width: 20, column: 5 };
        }

        this.setState({ formLayout, width });
    }
}

export default CardTable;